import { useMutation } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@lawline/ui/components/dialog";

import { updateEndpointMutation } from "../webhooks.mutations";
import type { WebhookEndpoint } from "../webhooks.types";
import { endpointChange, endpointFields } from "../webhooks.utils";
import { WebhookEndpointForm } from "./webhook-endpoint-form";

export function EditWebhookEndpoint({ endpoint }: { endpoint: WebhookEndpoint }) {
	const t = useTranslations("webhooks.edit");
	const update = useMutation(updateEndpointMutation);
	const [open, setOpen] = useState(false);

	function onOpenChange(next: boolean) {
		setOpen(next);
		if (!next) update.reset();
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogTrigger asChild>
				<Button variant="outline" size="sm">
					<Pencil className="size-4" />
					{t("button")}
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("title")}</DialogTitle>
					<DialogDescription>{t("description")}</DialogDescription>
				</DialogHeader>
				<WebhookEndpointForm
					initial={endpointFields(endpoint)}
					pending={update.isPending}
					error={update.error}
					submitLabel={t("submit")}
					pendingLabel={t("submitLoading")}
					secondary={
						<Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
							{t("cancel")}
						</Button>
					}
					onSubmit={(fields) => {
						const change = endpointChange(endpoint, fields);
						if (Object.keys(change).length === 0) return onOpenChange(false);
						update.mutate({ id: endpoint.id, ...change }, { onSuccess: () => onOpenChange(false) });
					}}
				/>
			</DialogContent>
		</Dialog>
	);
}
